import { initClient, initContract } from '@ts-rest/core';
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse, isAxiosError, Method } from 'axios';
import isElectron from 'is-electron';
import omitBy from 'lodash/omitBy';
import qs from 'qs';

import i18n from '/@/i18n/i18n';
import {
    acceptedTokenRenewal,
    AuthSessionRef,
    createAuthFailureScheduler,
    createSharedRefresh,
    ndRefreshKey,
} from '/@/renderer/api/auth-session';
import { validateResponse } from '/@/renderer/api/response-validation';
import { authenticationFailure, isRequestSessionCurrent } from '/@/renderer/api/utils';
import { useAuthStore } from '/@/renderer/store';
import { logger } from '/@/renderer/utils/logger';
import { getServerUrl } from '/@/renderer/utils/normalize-server-url';
import { ndType } from '/@/shared/api/navidrome/navidrome-types';
import { resultWithHeaders } from '/@/shared/api/utils';
import { toast } from '/@/shared/components/toast/toast';
import { ServerListItemWithCredential } from '/@/shared/types/domain-types';

const localSettings = isElectron() ? window.api.localSettings : null;

const c = initContract();

export const contract = c.router({
    addToPlaylist: {
        body: ndType._parameters.addToPlaylist,
        method: 'POST',
        path: 'playlist/:id/tracks',
        responses: {
            200: resultWithHeaders(ndType._response.addToPlaylist),
            500: resultWithHeaders(ndType._response.error),
        },
    },
    authenticate: {
        body: ndType._parameters.authenticate,
        method: 'POST',
        path: 'auth/login',
        responses: {
            200: resultWithHeaders(ndType._response.authenticate),
            500: resultWithHeaders(ndType._response.error),
        },
    },
    createPlaylist: {
        body: ndType._parameters.createPlaylist,
        method: 'POST',
        path: 'playlist',
        responses: {
            200: resultWithHeaders(ndType._response.createPlaylist),
            500: resultWithHeaders(ndType._response.error),
        },
    },
    deleteArtistImage: {
        body: null,
        method: 'DELETE',
        path: 'artist/:id/image',
        responses: {
            200: resultWithHeaders(ndType._response.deleteArtistImage),
            500: resultWithHeaders(ndType._response.error),
        },
    },
    deleteInternetRadioStation: {
        body: null,
        method: 'DELETE',
        path: 'radio/:id',
        responses: {
            200: resultWithHeaders(ndType._response.deleteInternetRadioStation),
            500: resultWithHeaders(ndType._response.error),
        },
    },
    deleteInternetRadioStationImage: {
        body: null,
        method: 'DELETE',
        path: 'radio/:id/image',
        responses: {
            200: resultWithHeaders(ndType._response.deleteInternetRadioStationImage),
            500: resultWithHeaders(ndType._response.error),
        },
    },
    deletePlaylist: {
        body: null,
        method: 'DELETE',
        path: 'playlist/:id',
        responses: {
            200: resultWithHeaders(ndType._response.deletePlaylist),
            500: resultWithHeaders(ndType._response.error),
        },
    },
    deletePlaylistImage: {
        body: null,
        method: 'DELETE',
        path: 'playlist/:id/image',
        responses: {
            200: resultWithHeaders(ndType._response.deletePlaylistImage),
            500: resultWithHeaders(ndType._response.error),
        },
    },
    getAlbumArtistDetail: {
        method: 'GET',
        path: 'artist/:id',
        responses: {
            200: resultWithHeaders(ndType._response.albumArtist),
            500: resultWithHeaders(ndType._response.error),
        },
    },
    getAlbumArtistList: {
        method: 'GET',
        path: 'artist',
        query: ndType._parameters.albumArtistList,
        responses: {
            200: resultWithHeaders(ndType._response.albumArtistList),
            500: resultWithHeaders(ndType._response.error),
        },
    },
    getAlbumDetail: {
        method: 'GET',
        path: 'album/:id',
        responses: {
            200: resultWithHeaders(ndType._response.album),
            500: resultWithHeaders(ndType._response.error),
        },
    },
    getAlbumList: {
        method: 'GET',
        path: 'album',
        query: ndType._parameters.albumList,
        responses: {
            200: resultWithHeaders(ndType._response.albumList),
            500: resultWithHeaders(ndType._response.error),
        },
    },
    getGenreList: {
        method: 'GET',
        path: 'genre',
        query: ndType._parameters.genreList,
        responses: {
            200: resultWithHeaders(ndType._response.genreList),
            500: resultWithHeaders(ndType._response.error),
        },
    },
    getPlaylistDetail: {
        method: 'GET',
        path: 'playlist/:id',
        responses: {
            200: resultWithHeaders(ndType._response.playlist),
            500: resultWithHeaders(ndType._response.error),
        },
    },
    getPlaylistList: {
        method: 'GET',
        path: 'playlist',
        query: ndType._parameters.playlistList,
        responses: {
            200: resultWithHeaders(ndType._response.playlistList),
            500: resultWithHeaders(ndType._response.error),
        },
    },
    getPlaylistSongList: {
        method: 'GET',
        path: 'playlist/:id/tracks',
        query: ndType._parameters.songList,
        responses: {
            200: resultWithHeaders(ndType._response.playlistSongList),
            500: resultWithHeaders(ndType._response.error),
        },
    },
    getQueue: {
        method: 'GET',
        path: 'queue',
        responses: {
            200: resultWithHeaders(ndType._response.queue),
            500: resultWithHeaders(ndType._response.error),
        },
    },
    getRadioList: {
        method: 'GET',
        path: 'radio',
        query: ndType._parameters.radioList,
        responses: {
            200: resultWithHeaders(ndType._response.radioList),
            500: resultWithHeaders(ndType._response.error),
        },
    },
    getSongDetail: {
        method: 'GET',
        path: 'song/:id',
        responses: {
            200: resultWithHeaders(ndType._response.song),
            500: resultWithHeaders(ndType._response.error),
        },
    },
    getSongList: {
        method: 'GET',
        path: 'song',
        query: ndType._parameters.songList,
        responses: {
            200: resultWithHeaders(ndType._response.songList),
            500: resultWithHeaders(ndType._response.error),
        },
    },
    getTagList: {
        method: 'GET',
        path: 'tag',
        query: ndType._parameters.tagList,
        responses: {
            200: resultWithHeaders(ndType._response.tagList),
            500: resultWithHeaders(ndType._response.error),
        },
    },
    getUserList: {
        method: 'GET',
        path: 'user',
        query: ndType._parameters.userList,
        responses: {
            200: resultWithHeaders(ndType._response.userList),
            500: resultWithHeaders(ndType._response.error),
        },
    },
    movePlaylistItem: {
        body: ndType._parameters.moveItem,
        method: 'PUT',
        path: 'playlist/:playlistId/tracks/:trackNumber',
        responses: {
            200: resultWithHeaders(ndType._response.moveItem),
            400: resultWithHeaders(ndType._response.error),
        },
    },
    removeFromPlaylist: {
        body: null,
        method: 'DELETE',
        path: 'playlist/:id/tracks',
        query: ndType._parameters.removeFromPlaylist,
        responses: {
            200: resultWithHeaders(ndType._response.removeFromPlaylist),
            500: resultWithHeaders(ndType._response.error),
        },
    },
    saveQueue: {
        body: ndType._parameters.saveQueue,
        method: 'POST',
        path: 'queue',
        responses: {
            200: resultWithHeaders(ndType._response.saveQueue),
            500: resultWithHeaders(ndType._response.error),
        },
    },
    shareItem: {
        body: ndType._parameters.shareItem,
        method: 'POST',
        path: 'share',
        responses: {
            200: resultWithHeaders(ndType._response.shareItem),
            404: resultWithHeaders(ndType._response.error),
            500: resultWithHeaders(ndType._response.error),
        },
    },
    updateInternetRadioStation: {
        body: ndType._parameters.updateInternetRadioStation,
        method: 'PUT',
        path: 'radio/:id',
        responses: {
            200: resultWithHeaders(ndType._response.updateInternetRadioStation),
            500: resultWithHeaders(ndType._response.error),
        },
    },
    updatePlaylist: {
        body: ndType._parameters.updatePlaylist,
        method: 'PUT',
        path: 'playlist/:id',
        responses: {
            200: resultWithHeaders(ndType._response.updatePlaylist),
            500: resultWithHeaders(ndType._response.error),
        },
    },
    uploadArtistImage: {
        body: ndType._parameters.uploadArtistImage,
        method: 'POST',
        path: 'artist/:id/image',
        responses: {
            200: resultWithHeaders(ndType._response.uploadArtistImage),
            500: resultWithHeaders(ndType._response.error),
        },
    },
    uploadInternetRadioStationImage: {
        body: ndType._parameters.uploadInternetRadioStationImage,
        method: 'POST',
        path: 'radio/:id/image',
        responses: {
            200: resultWithHeaders(ndType._response.uploadInternetRadioStationImage),
            500: resultWithHeaders(ndType._response.error),
        },
    },
    uploadPlaylistImage: {
        body: ndType._parameters.uploadPlaylistImage,
        method: 'POST',
        path: 'playlist/:id/image',
        responses: {
            200: resultWithHeaders(ndType._response.uploadPlaylistImage),
            500: resultWithHeaders(ndType._response.error),
        },
    },
});

const axiosClient = axios.create({});

axiosClient.defaults.paramsSerializer = (params) => {
    return qs.stringify(params, { arrayFormat: 'repeat' });
};

const parsePath = (fullPath: string) => {
    const [path, params] = fullPath.split('?');

    const parsedParams = qs.parse(params);

    // Convert indexed object to array
    const newParams: Record<string, any> = {};
    Object.keys(parsedParams).forEach((key) => {
        const isIndexedArrayObject =
            typeof parsedParams[key] === 'object' &&
            Object.keys(parsedParams[key] || {}).includes('0');

        if (!isIndexedArrayObject) {
            newParams[key] = parsedParams[key];
        } else {
            newParams[key] = Object.values(parsedParams[key] || {});
        }
    });

    const notNilParams = omitBy(newParams, (value) => value === 'undefined' || value === 'null');

    return {
        params: notNilParams,
        path,
    };
};

/** A Navidrome request bound to the sign-in it was sent with (see `auth-session.ts`). */
type SessionRequestConfig = AxiosRequestConfig & {
    _authRetried?: boolean;
    _authSession?: AuthSessionRef;
};

const AUTH_FAILURE_DELAY_MS = 1000;

/** The login was rate limited; already reported, nothing else to say. */
const RATE_LIMITED_ERROR = new Error('Navidrome login rate limited');
/** The sign-in changed while its token was being refreshed. */
const SESSION_CHANGED_ERROR = new Error('Navidrome sign-in changed during reauthentication');
/** No saved password to sign in again with. */
const NO_SAVED_PASSWORD_ERROR = new Error('No saved password for reauthentication');

const failures = createAuthFailureScheduler({
    delayMs: AUTH_FAILURE_DELAY_MS,
    fail: (ref) => {
        authenticationFailure(ref);
    },
    isCurrent: isRequestSessionCurrent,
});

/** Token refreshes in flight, one per sign-in; resolves to the new Navidrome token. */
const refreshes = createSharedRefresh<string>();

// A new sign-in (or a logout, or another server) makes a pending failure of
// the previous one meaningless: drop it right away instead of at fire time.
useAuthStore.subscribe((state, previous) => {
    if (state.currentServer !== previous.currentServer) failures.cancelIfStale();
});

const storedServer = (serverId: string) => {
    const state = useAuthStore.getState();
    return state.currentServer?.id === serverId
        ? state.currentServer
        : (state.serverList[serverId] ?? null);
};

/**
 * Sign the sign-in `ref` in again with the saved password (Electron only) and
 * store the new tokens, unless that sign-in stopped being the stored one in
 * the meantime. Resolves to the new Navidrome token.
 */
const reauthenticate = async (ref: AuthSessionRef): Promise<string> => {
    const server = storedServer(ref.serverId);
    if (!localSettings || !server?.savePassword || !isRequestSessionCurrent(ref)) {
        throw NO_SAVED_PASSWORD_ERROR;
    }

    const password: null | string = await localSettings.passwordGet(server.id);
    if (password === null) throw NO_SAVED_PASSWORD_ERROR;

    // Do not use axiosClient: its interceptors would treat a failed login as a session failure.
    const res = await axios.post(
        `${server.url}/auth/login`,
        { password, username: server.username },
        { validateStatus: () => true },
    );

    if (!isRequestSessionCurrent(ref)) throw SESSION_CHANGED_ERROR;

    if (res.status === 429) {
        toast.error({
            message: i18n.t('error.loginRateError') as string,
            title: i18n.t('error.sessionExpiredError') as string,
        });

        useAuthStore.getState().actions.updateServer(server.id, {
            credential: undefined,
            ndCredential: undefined,
        });
        if (useAuthStore.getState().currentServer?.id === server.id) {
            useAuthStore.getState().actions.setCurrentServer(null);
        }

        // Already reported: stop queued failures from adding a second message.
        failures.cancel();
        throw RATE_LIMITED_ERROR;
    }

    if (res.status !== 200) {
        throw new Error(i18n.t('error.authenticatedFailed') as string);
    }

    const newCredential = res.data.token as string;
    const subsonicCredential = `u=${server.username}&s=${res.data.subsonicSalt}&t=${res.data.subsonicToken}`;

    useAuthStore.getState().actions.updateServer(server.id, {
        credential: subsonicCredential,
        ndCredential: newCredential,
    });

    return newCredential;
};

axiosClient.interceptors.response.use(
    (response) => {
        const ref = (response.config as SessionRequestConfig)._authSession;

        if (ref) {
            const renewed = acceptedTokenRenewal(
                ref,
                storedServer(ref.serverId),
                response.headers['x-nd-authorization'],
            );

            if (renewed) {
                useAuthStore.getState().actions.updateServer(ref.serverId, {
                    ndCredential: renewed,
                });
            }

            // This sign-in just worked: a failure queued for it is no longer true.
            failures.cancelFor(ref);
        }

        return response;
    },
    async (error) => {
        if (!error.response || error.response.status !== 401) {
            throw error;
        }

        const config = error.config as SessionRequestConfig | undefined;
        const ref = config?._authSession;

        // A request without a sign-in (a login attempt) or of an earlier sign-in
        // says nothing about the session the user has now.
        if (!config || !ref || !isRequestSessionCurrent(ref)) {
            throw error;
        }

        const server = storedServer(ref.serverId);

        if (localSettings && server?.savePassword && !config._authRetried) {
            try {
                const newCredential = await refreshes.run(ndRefreshKey(ref), () =>
                    reauthenticate(ref),
                );

                config._authRetried = true;
                config._authSession = { ndCredential: newCredential, serverId: ref.serverId };
                if (config.headers) {
                    config.headers['x-nd-authorization'] = `Bearer ${newCredential}`;
                }

                return axiosClient.request(config);
            } catch (refreshError) {
                if (refreshError === RATE_LIMITED_ERROR || refreshError === SESSION_CHANGED_ERROR) {
                    throw error;
                }

                if (isAxiosError(refreshError) && refreshError.code === 'ERR_NETWORK') {
                    logger.warn('Network error during reauthentication - preserving credentials');
                    throw error;
                }

                if (refreshError !== NO_SAVED_PASSWORD_ERROR) {
                    logger.error('Reauthentication failed', {
                        error: (refreshError as Error)?.message,
                        serverId: ref.serverId,
                    });
                }
            }
        }

        failures.schedule(ref);
        throw error;
    },
);

export const ndApiClient = (args: {
    forceRemoteUrl?: boolean;
    server: null | ServerListItemWithCredential;
    signal?: AbortSignal;
    url?: string;
}) => {
    const { forceRemoteUrl, server, signal, url } = args;

    return initClient(contract, {
        api: async ({ body, headers, method, path, route }) => {
            let baseUrl: string | undefined;
            let token: string | undefined;

            const { params, path: api } = parsePath(path);

            if (server) {
                const serverUrl = getServerUrl(server, forceRemoteUrl);
                baseUrl = serverUrl ? `${serverUrl}/api` : undefined;
                token = server?.ndCredential;
            } else {
                baseUrl = url;
            }

            try {
                let session: AuthSessionRef | undefined =
                    server && token ? { ndCredential: token, serverId: server.id } : undefined;

                // While this sign-in's token is being refreshed, wait for it and
                // send the new token instead of collecting another 401.
                const refresh = session ? refreshes.pending(ndRefreshKey(session)) : undefined;
                if (refresh && session) {
                    const renewed = await refresh.catch(() => null);
                    if (renewed) {
                        token = renewed;
                        session = { ndCredential: renewed, serverId: session.serverId };
                    }
                }

                const request: SessionRequestConfig = {
                    _authSession: session,
                    data: body,
                    headers: {
                        ...headers,
                        ...(token && { 'x-nd-authorization': `Bearer ${token}` }),
                    },
                    method: method as Method,
                    params,
                    signal,
                    url: `${baseUrl}/${api}`,
                };
                const result = await axiosClient.request(request);
                validateResponse({
                    controller: 'Navidrome',
                    method,
                    path: api,
                    response: { data: result.data, headers: result.headers },
                    route,
                    status: result.status,
                });
                return {
                    body: { data: result.data, headers: result.headers },
                    headers: result.headers as any,
                    status: result.status,
                };
            } catch (e: any | AxiosError | Error) {
                if (isAxiosError(e)) {
                    if (e.code === 'ERR_NETWORK') {
                        throw new Error(i18n.t('error.networkError') as string);
                    }

                    const error = e as AxiosError;
                    const response = error.response as AxiosResponse;
                    validateResponse({
                        controller: 'Navidrome',
                        method,
                        path: api,
                        response: { data: response?.data, headers: response?.headers },
                        route,
                        status: response?.status,
                    });
                    return {
                        body: { data: response?.data, headers: response?.headers },
                        headers: response?.headers as any,
                        status: response?.status,
                    };
                }
                throw e;
            }
        },
        baseHeaders: {
            'Content-Type': 'application/json',
        },
        baseUrl: '',
        jsonQuery: false,
    });
};
